import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ActionIcon, Group, Loader, ScrollArea, Stack } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useUpdateProject from 'src/Hooks/Mutations/useUpdateProject';
import useFetchProjects from 'src/Hooks/Queries/useFetchProjects';
import { CreateProjectDtoStatusEnum } from 'src/Services/API/generated/api';

import { ProjectCard } from './ProjectCard';
import styles from './ProjectKanban.module.scss';
import { Props, ColumnProps } from './ProjectKanban.types';

const KanbanColumn = ({ status, projects, title, assetId }: ColumnProps) => {
  const navigate = useNavigate();
  const { setNodeRef } = useDroppable({
    id: status,
  });

  const handleAddProject = () => {
    navigate(`/asset/${assetId}/project-and-works/new?status=${status}`);
  };

  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <Group gap="xs">
          {title}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={handleAddProject}
            className={styles.addButton}
          >
            <IconPlus size={16} />
          </ActionIcon>
        </Group>
        <Group gap="xs">
          <span className={styles.columnCount}>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
              projects.reduce((acc, curr) => acc + (curr.estimatedCost || 0), 0)
            )}
          </span>
          <span className={styles.columnCount}>{projects.length}</span>
        </Group>
      </div>
      <div className={styles.columnContent}>
        <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
          <Stack gap="md" ref={setNodeRef} className={styles.columnList}>
            {projects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </Stack>
        </SortableContext>
      </div>
    </div>
  );
};

const ProjectKanban = ({ assetId }: Props) => {
  const { t } = useTranslation();
  const { data: projects, isLoading } = useFetchProjects(assetId);
  const { mutate: updateProject } = useUpdateProject(assetId);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const columns: { status: CreateProjectDtoStatusEnum; title: string }[] = [
    {
      status: CreateProjectDtoStatusEnum.IdeaBox,
      title: t('projects.status.ideaBox', 'Boîte à idées'),
    },
    {
      status: CreateProjectDtoStatusEnum.IdeaToVote,
      title: t('projects.status.ideaToVote', 'À voter'),
    },
    {
      status: CreateProjectDtoStatusEnum.ProjectValidated,
      title: t('projects.status.projectValidated', 'Validé'),
    },
    {
      status: CreateProjectDtoStatusEnum.ProjectInProgress,
      title: t('projects.status.projectInProgress', 'En cours'),
    },
    {
      status: CreateProjectDtoStatusEnum.ProjectCompleted,
      title: t('projects.status.projectCompleted', 'Terminé'),
    },
  ];

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const project = projects?.find(p => p.id === active.id);
      if (!project) return;

      const overContainerId = over.id as string;
      const isColumn = Object.values(CreateProjectDtoStatusEnum).includes(
        overContainerId as CreateProjectDtoStatusEnum
      );

      let newStatus: CreateProjectDtoStatusEnum | null = null;

      if (isColumn) {
        newStatus = overContainerId as CreateProjectDtoStatusEnum;
      } else {
        // Dropped on another card — find which column that card belongs to
        const overProject = projects?.find(p => p.id === over.id);
        if (overProject) {
          newStatus = overProject.status as CreateProjectDtoStatusEnum;
        }
      }

      if (newStatus && project.status !== newStatus) {
        updateProject({
          projectId: project.id,
          data: { status: newStatus },
        });
      }
    }
    setActiveId(null);
  };

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  const activeProject = activeId ? projects?.find(p => p.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <ScrollArea className={styles.scrollArea} type="auto" offsetScrollbars="x">
        <Group align="flex-start" wrap="nowrap" p="md" gap="md" className={styles.kanbanContainer}>
          {columns.map(col => (
            <KanbanColumn
              key={col.status}
              status={col.status}
              title={col.title}
              projects={projects?.filter(p => p.status === col.status) || []}
              assetId={assetId}
            />
          ))}
        </Group>
      </ScrollArea>
      <DragOverlay>{activeProject ? <ProjectCard project={activeProject} /> : null}</DragOverlay>
    </DndContext>
  );
};

export default ProjectKanban;
